import React, { useCallback, useEffect, useState, memo } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Dropdown, { DropdownOption } from "../../../components/inputs/Dropdown";

import { getAllStationIds } from "../../../services/clearing/stations";
import {
  getStationsByCustomerId,
  removeStationFromCustomer,
  assignStationToCustomer,
} from "../../../services/clearing/customers";
import { useNavigate } from "react-router-dom";

interface CustomerStationsProps {
  customerId: string;
}

interface OwnedStationListProps {
  ownedStations: string[];
  onRemoveStation: (stationId: string) => void;
}

interface AssignStationDialogProps {
  visible: boolean;
  availableStations: DropdownOption[];
  selectedStation: DropdownOption | null;
  onStationChange: (station: DropdownOption | null) => void;
  onAssign: () => void;
  onHide: () => void;
}

const OwnedStationList: React.FC<OwnedStationListProps> = memo(
  ({ ownedStations, onRemoveStation }) => {
    const navigate = useNavigate();
    return (
      <>
        {ownedStations.map((stationId) => (
          <div className="flex gap-2 align-items-center mb-3">
            <p key={stationId} className="text-2xl font-semibold mb-0">
              {stationId}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onRemoveStation(stationId);
                }}
                icon="pi pi-trash"
                rounded
                className="p-button-danger"
              />
              <Button icon="pi pi-external-link" rounded onClick={() => {
                navigate(`/station-details/${stationId}`);
              }} />
            </div>
          </div>
        ))}
      </>
    );
  }
);

const AssignStationButton: React.FC<{ onClick: () => void }> = memo(
  ({ onClick }) => {
    return (
      <div className="mt-4 flex gap-2">
        <Button
          style={{ margin: "10px" }}
          label="Assign station to customer"
          icon="pi pi-plus"
          className="p-button-primary"
          onClick={onClick}
        />
      </div>
    );
  }
);

const AssignStationDialog: React.FC<AssignStationDialogProps> = memo(
  ({
    visible,
    availableStations,
    selectedStation,
    onStationChange,
    onAssign,
    onHide,
  }) => {
    return (
      <Dialog
        header="Assign Station"
        visible={visible}
        style={{ width: "30rem" }}
        onHide={onHide}
      >
        <div className="p-fluid">
          <div className="p-field mb-4">
            <label htmlFor="stationId" className="mb-2 block">
              Station ID
            </label>
            <Dropdown
              options={availableStations}
              value={selectedStation}
              onChange={onStationChange}
              placeholder="Select a Station"
              className="w-full"
              searchable
            />
          </div>
          <Button
            label="Assign"
            icon="pi pi-check"
            className="p-button-primary"
            onClick={onAssign}
            disabled={!selectedStation}
          />
        </div>
      </Dialog>
    );
  }
);

const CustomerStations: React.FC<CustomerStationsProps> = ({ customerId }) => {
  const [ownedStations, setOwnedStations] = useState<string[] | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [availableStations, setAvailableStations] = useState<DropdownOption[]>(
    []
  );
  const [selectedStation, setSelectedStation] = useState<DropdownOption | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const fetchStations = useCallback(async () => {
    if (customerId) {
      const stationsList =
        (await getStationsByCustomerId({ customerId })) || [];
      setOwnedStations(stationsList);
    }
  }, [customerId]);

  const fetchAvailableStations = useCallback(async () => {
    const allStations = (await getAllStationIds()) || [];
    const stationOptions = allStations.map((id: string) => ({
      name: id,
      code: id,
    }));
    setAvailableStations(stationOptions);
  }, []);

  const removeStation = useCallback(async (stationId: string) => {
    if (stationId) {
      const success = await removeStationFromCustomer({ stationId });
      if (success) {
        setOwnedStations((current) => {
          if (!current) return current;
          const index = current.indexOf(stationId);
          if (index !== -1) {
            const updated = [...current];
            updated.splice(index, 1);
            return updated;
          }
          return current;
        });
      }
    }
  }, []);

  const handleAssignStation = useCallback(async () => {
    if (selectedStation && customerId) {
      try {
        const response = await assignStationToCustomer({
          customerId,
          stationId: selectedStation.code,
        });
        const newStationId =
          typeof response === "string" ? response : String(selectedStation.code);
        setOwnedStations((current) => {
          if (!current) return [newStationId];
          if (!current.includes(newStationId)) {
            return [...current, newStationId];
          }
          return current;
        });
        setShowAssignDialog(false);
        setSelectedStation(null);
        setError(null);
      } catch (err) {
        setError("Failed to assign station. Please try again.");
      }
    }
  }, [selectedStation, customerId]);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  useEffect(() => {
    if (showAssignDialog) {
      fetchAvailableStations();
    }
  }, [showAssignDialog, fetchAvailableStations]);

  if (!ownedStations) return <p className="p-4">Loading owned stations ...</p>;

  return (
    <div className="p-4">
      <Card
        className="-mx-4 px-2"
        title="Owned stations"
        subTitle={`CustomerId: ${customerId}`}
      >
        <OwnedStationList
          ownedStations={ownedStations}
          onRemoveStation={removeStation}
        />
        {error && <p className="text-red-500 mt-2">{error}</p>}
        <AssignStationButton onClick={() => setShowAssignDialog(true)} />
      </Card>

      <AssignStationDialog
        visible={showAssignDialog}
        availableStations={availableStations}
        selectedStation={selectedStation}
        onStationChange={setSelectedStation}
        onAssign={handleAssignStation}
        onHide={() => {
          setShowAssignDialog(false);
          setSelectedStation(null);
          setError(null);
        }}
      />
    </div>
  );
};

export default CustomerStations;
